import * as R from "ramda";

import {
  SCHEDULE_RESULTS_FIELDS,
  SCHEDULE,
  OPTIMISE,
  RESULTS,
} from "../constants/strings";
import stopTypes from "../enums/stopTypes";
import {
  fromMinutesToTime,
  toMinutes,
  toSeconds,
  fullTimeToShort,
  formatSecondsToHHMM,
  formatTimeStringWithDefaultMaxValue,
} from "../utils/date";
import { isBreakStop } from "./stop";

export const compareStopsBySeqNo = (s1, s2) =>
  s1.sequenceNumber - s2.sequenceNumber;

export const getSortedNonBreakStops = (routeDetail) =>
  (routeDetail &&
    routeDetail?.stop &&
    routeDetail.stop
      .filter((stop) => !isBreakStop(stop))
      .sort(compareStopsBySeqNo)) ||
  [];

export const getBreakStop = (routeDetail) => routeDetail.stop.find(isBreakStop);

export const calculateLunchStopNo = (sortedStops, breakStopSeqNumb) => {
  let lunchStopNo = 0;

  for (let i = sortedStops.length - 1; i >= 0; i--) {
    if (sortedStops[i].sequenceNumber < breakStopSeqNumb) {
      lunchStopNo = +sortedStops[i].stopNumber;
    }
  }

  return lunchStopNo || +sortedStops[0].stopNumber;
};

export const getLunchStopNo = (sortedStops, breakStop) => {
  const invalidLunchStopNo = 0;

  if (breakStop && breakStop.sequenceNumber && sortedStops.length) {
    return calculateLunchStopNo(sortedStops, breakStop.sequenceNumber);
  }

  return invalidLunchStopNo;
};

export const getLunchTimes = (breakStop) => {
  if (!breakStop) {
    return { lunchStart: null, lunchDuration: null };
  }
  const start = toSeconds(breakStop.serviceStartTime);
  const end = toSeconds(breakStop.serviceEndTime);
  return {
    lunchStart: fullTimeToShort(breakStop.serviceStartTime),
    lunchDuration: end - start,
  };
};

export const isDowngradeRequired = (
  { lastWaveRoute },
  optimise,
  { downgradeCutOff, routePlannerDowngrades }
) => {
  if (routePlannerDowngrades && downgradeCutOff) {
    const currentTime = new Date().valueOf();
    const timeArr = downgradeCutOff.split(":");
    const downgradeTime = new Date().setHours(
      timeArr[0],
      timeArr[1],
      timeArr[2]
    );
    return currentTime > downgradeTime && lastWaveRoute && optimise;
  }
  return false;
};

export const formatScheduleData = (routeDetail, optimise) => {
  const sortedStops = getSortedNonBreakStops(routeDetail);
  const breakStop = getBreakStop(routeDetail);
  const lunchStopNumber = getLunchStopNo(sortedStops, breakStop);
  const { lunchStart, lunchDuration } = getLunchTimes(breakStop);
  return {
    optimise,
    lunchDuration,
    lunchStart,
    lunchStopNumber,
    driverCode: routeDetail.driverCode,
    routeDate: routeDetail.routeDate,
    checksum: routeDetail.checksum,
    routeStatus: routeDetail.status,
    startTime: routeDetail.routeStartTime,
    stop: sortedStops.map((stop) => {
      return {
        stopNumber: stop.stopNumber,
        serviceStartTime: stop.serviceStartTime,
        serviceEndTime: stop.serviceEndTime,
        stopTime: stop.stopTime,
        stopConsolidated: stop.consolidated,
        stopLocation: {
          latitude: stop.stopAddress?.latitude,
          longitude: stop.stopAddress?.longitude,
        },
        postcode: stop.stopAddress?.postcode,
        stopType: `${stop.stopType ?? ""}`,
        priority: stop.priority,
      };
    }),
  };
};

export const findSchedulingStop = (scheduleStops, { stopNumber }) =>
  scheduleStops.find(
    (scheduleStop) => +scheduleStop.originalStopNumber === +stopNumber
  );

const findBreakStop = (scheduleStops) =>
  scheduleStops.find(
    (scheduleStop) => scheduleStop.stopType === stopTypes.break
  );

export const scheduleStopMapper = (stops, scheduledStops, optimise) =>
  stops.map((stop) => {
    if (isBreakStop(stop) && optimise) {
      const breakStop = findBreakStop(scheduledStops);
      return breakStop
        ? {
            ...stop,
            sequenceNumber: breakStop.sequenceNumber,
            displayNumber: `${breakStop.sequenceNumber}`,
          }
        : stop;
    }

    if (isBreakStop(stop) && !optimise) {
      return stop;
    }

    const scheduledStop = findSchedulingStop(scheduledStops, stop);
    const updatedStop = R.pick(
      ["eta", "opportunityTime", "exception"],
      scheduledStop
    );
    if (optimise) {
      updatedStop.sequenceNumber = scheduledStop.sequenceNumber;
      updatedStop.displayNumber = `${scheduledStop.sequenceNumber}`;
    }
    return {
      ...stop,
      ...updatedStop,
    };
  });

export const updateRouteDetailByScheduleData = (
  routeDetail,
  scheduleData,
  optimise = false
) => ({
  ...routeDetail,
  totalMiles: scheduleData.mileage,
  routeEndTime: formatTimeStringWithDefaultMaxValue(scheduleData.returnToDepot),
  opportunityTime: scheduleData.totalRouteOpportunityTime,
  returnToDepotOpportunityTime: scheduleData.returnToDepotOpportunityTime,
  returnToDepot: scheduleData.returnToDepot,
  stop: scheduleStopMapper(routeDetail.stop, scheduleData.stop, optimise),
});

const diffToStr = (diff) => (diff >= 0 ? `( +${diff} )` : `( ${diff} )`);

const timeDiffToStr = (diff, formatFn = fromMinutesToTime) =>
  diff < 0 ? `( -${formatFn(-diff)} )` : `( +${formatFn(diff)} )`;

const mapExceptions = (stops) => {
  const byTypes = {
    Postcode: 0,
    Service: 0,
    Duty: 0,
    Range: 0,
    "Legal Duty": 0,
  };
  const total = stops.reduce((acc, stop) => {
    const exceptionCode = +stop.exception;
    if (exceptionCode) {
      acc += 1;
      switch (exceptionCode) {
        case 1:
          byTypes.Postcode += 1;
          break;
        case 2:
          byTypes.Service += 1;
          break;
        case 3:
          byTypes.Duty += 1;
          break;
        case 6:
          byTypes.Range += 1;
          break;
        case 7:
          byTypes["Legal Duty"] += 1;
          break;
        default:
          break;
      }
    }
    return acc;
  }, 0);
  return {
    total,
    byTypes,
  };
};

const getLastStopEndTime = (sortedStops) => {
  const lastStop = sortedStops[sortedStops.length - 1];
  if (!lastStop?.eta) {
    return 0;
  }
  const lastStopEta = toMinutes(lastStop.eta);
  return lastStop?.stopTime ? lastStopEta + lastStop.stopTime : lastStopEta;
};

const setMileage = (totalMiles, mileage) => ({
  name: SCHEDULE_RESULTS_FIELDS.MILEAGE,
  before: totalMiles,
  after: mileage,
  diff: diffToStr(mileage - totalMiles),
});

const setOpportunityTime = (opportunityTime, totalRouteOpportunityTime) => {
  const diff = totalRouteOpportunityTime - opportunityTime;
  return {
    name: SCHEDULE_RESULTS_FIELDS.OPPORTUNITY,
    before: formatSecondsToHHMM(opportunityTime),
    after: formatSecondsToHHMM(totalRouteOpportunityTime),
    diff: timeDiffToStr(diff, formatSecondsToHHMM),
  };
};

const setExceptions = (stops, scheduledStops, totalExceptions) => {
  const exceptions = mapExceptions(stops);
  const scheduleExceptions = mapExceptions(scheduledStops);
  return {
    name: SCHEDULE_RESULTS_FIELDS.EXCEPTIONS,
    before: totalExceptions,
    beforeExceptions: exceptions.byTypes,
    after: scheduleExceptions.total,
    afterExceptions: scheduleExceptions.byTypes,
  };
};

const setDepotToFirstStop = (sortedStops, routeStartTime, scheduleStops) => {
  const startTime = toMinutes(routeStartTime);
  const firstStopEta = toMinutes(sortedStops[0]?.eta || routeStartTime);
  const firstScheduledStopEta = toMinutes(scheduleStops[0]?.eta || "");
  const before = firstStopEta - startTime;
  const after = firstScheduledStopEta - startTime;
  return {
    name: SCHEDULE_RESULTS_FIELDS.DEPOT_TO_1ST_STOP,
    before: before ? fromMinutesToTime(before) : "",
    after: after ? fromMinutesToTime(after) : "",
    diff: before && after ? diffToStr(after - before) : "",
  };
};

const setLastStopEndTime = (sortedStops, scheduledStops) => {
  const before = sortedStops.length && getLastStopEndTime(sortedStops);
  const after = scheduledStops.length && getLastStopEndTime(scheduledStops);

  return {
    name: SCHEDULE_RESULTS_FIELDS.LAST_STOP_END_TIME,
    before: before ? fromMinutesToTime(before) : "",
    after: after ? fromMinutesToTime(after) : "",
    diff: before && after ? timeDiffToStr(after - before) : "",
  };
};

const setLastStopToDepot = (
  sortedStops,
  scheduledStops,
  routeEndTime,
  returnToDepot
) => {
  const before = toMinutes(routeEndTime) - getLastStopEndTime(sortedStops);
  const after = toMinutes(returnToDepot) - getLastStopEndTime(scheduledStops);

  return {
    name: SCHEDULE_RESULTS_FIELDS.LAST_STOP_TO_DEPOT,
    before: fromMinutesToTime(before),
    after: fromMinutesToTime(after),
    diff: timeDiffToStr(after - before),
  };
};

const setReturnToDepot = (routeEndTime, returnToDepot) => {
  const before = toMinutes(routeEndTime);
  const after = toMinutes(returnToDepot);
  return {
    name: SCHEDULE_RESULTS_FIELDS.RETURN_TO_DEPOT,
    before: fromMinutesToTime(before),
    after: fromMinutesToTime(after),
    diff: timeDiffToStr(after - before),
  };
};

export const formatScheduleResults = (routeDetail, scheduleResults) => {
  if (!scheduleResults || !routeDetail) {
    return { leftData: [], rightData: [] };
  }
  const returnToDepot = formatTimeStringWithDefaultMaxValue(
    scheduleResults.returnToDepot
  );
  const sortedStops = getSortedNonBreakStops(routeDetail);
  const scheduledStops = scheduleResults.stop;
  const leftData = [];
  const rightData = [];
  leftData.push(setMileage(routeDetail.totalMiles, scheduleResults.mileage));
  leftData.push(
    setOpportunityTime(
      routeDetail.opportunityTime,
      scheduleResults.totalRouteOpportunityTime
    )
  );
  leftData.push(
    setExceptions(sortedStops, scheduledStops, routeDetail.totalExceptions)
  );
  rightData.push(
    setDepotToFirstStop(sortedStops, routeDetail.routeStartTime, scheduledStops)
  );
  rightData.push(setLastStopEndTime(sortedStops, scheduledStops));
  rightData.push(
    setLastStopToDepot(
      sortedStops,
      scheduledStops,
      routeDetail.routeEndTime,
      returnToDepot
    )
  );
  rightData.push(setReturnToDepot(routeDetail.routeEndTime, returnToDepot));
  return { leftData, rightData };
};

export const getDrawerTitle = (optimise) => (optimise ? OPTIMISE : SCHEDULE);

export const getCardTitle = (title) => `${title} ${RESULTS}`;

export const hasServiceException = (stops) =>
  stops.some((stop) => stop.exception === 2);

export const getStopsWithServiceException = (stops = []) =>
  stops.filter((stop) => stop.exception === 2);

export const getServiceExceptionTotal = (stops) =>
  getStopsWithServiceException(stops).length;

const downgradeParcels = (parcels, parcelsForDowngrade) =>
  parcels.map((item) => {
    const downgradedParcel = parcelsForDowngrade.find(
      (parcel) => item.parcelCode === parcel.parcelCode
    );
    return downgradedParcel
      ? !item.serviceDowngraded
        ? {
            ...item,
            originalNetwork: item.networkCode,
            originalServiceStartTime: item.serviceStartTime,
            originalServiceEndTime: item.serviceEndTime,
            serviceStartTime: item.altServiceStartTime,
            serviceEndTime: item.altServiceEndTime,
            networkCode: item.altNetworkCode,
            serviceDowngraded: true,
          }
        : {
            ...item,
            serviceStartTime:
              item.downgradeServiceStartTime || item.altServiceStartTime,
            serviceEndTime:
              item.downgradeServiceEndTime || item.altServiceEndTime,
            networkCode: item.downgradeNetworkCode || item.altNetworkCode,
          }
      : item;
  });

export const downgradeStop = (stop) => {
  const { parcel } = stop;
  const shortStopEndTime = fullTimeToShort(stop.serviceEndTime);

  const parcelsForDowngrade = parcel.every(
    (item) => item.networkCode === parcel[0].networkCode
  )
    ? parcel.filter((item) => item.applyDowngrade)
    : parcel.filter(
        (item) =>
          item.applyDowngrade &&
          fullTimeToShort(item.serviceEndTime) === shortStopEndTime
      );
  const suitableParcel = parcelsForDowngrade.find(
    (item) =>
      (item.serviceDowngraded && item.downgradeNetworkCode) ||
      item.altNetworkCode
  );
  return suitableParcel
    ? {
        updatedStop: {
          ...stop,
          serviceStartTime:
            (suitableParcel.serviceDowngraded &&
              suitableParcel.downgradeServiceStartTime) ||
            suitableParcel.altServiceStartTime,
          serviceEndTime:
            (suitableParcel.serviceDowngraded &&
              suitableParcel.downgradeServiceEndTime) ||
            suitableParcel.altServiceEndTime,
          parcel: downgradeParcels(parcel, parcelsForDowngrade),
        },
        downgraded: true,
      }
    : { updatedStop: stop };
};

export const createDowngradedRoute = (stopsWithException, route) => {
  let routeDowngraded = false;
  const updatedStop = route.stop.map((stop) => {
    const stopToUpdate = stopsWithException.find(
      (item) => +item.originalStopNumber === +stop.stopNumber
    );
    if (stopToUpdate && stop?.parcel.length) {
      const { updatedStop, downgraded = false } = downgradeStop(stop);
      routeDowngraded = downgraded || routeDowngraded;
      return updatedStop;
    }
    return stop;
  });
  return routeDowngraded && { ...route, stop: updatedStop };
};
